import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Modal component to display NUTRIENT validation results and confirmation for import
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} props.onConfirm - Function to call when confirming the import
 * @param {Object} props.validationResult - Validation result data
 * @param {boolean} props.isImporting - Whether an import is in progress
 * @param {Object} props.importResult - Result of the import operation
 * @param {string} [props.itemType='Items'] - Text for the item type (e.g., 'Dishes')
 * @param {string} [props.confirmButtonText='Confirm Import'] - Text for the confirm button
 * @param {string} [props.successMessage='Import successful'] - Message on successful import
 * @param {string} [props.modalTitle='Validation Results'] - Title for the modal
 */
const NutrientValidationSummaryModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  validationResult,
  isImporting = false,
  importResult = null,
  // Props specific to nutrient modal
  itemType = 'Dishes', // Default to Dishes, but can be overridden by props
  confirmButtonText = 'Confirm Import', 
  successMessage = 'Import successful',
  modalTitle = 'Nutrient Validation Results' // Changed default title
}) => {
  const { t } = useTranslation();
  
  // State for pagination (New and Changed sections)
  const [currentPageNew, setCurrentPageNew] = useState(1);
  const [currentPageChanged, setCurrentPageChanged] = useState(1);
  
  const ITEMS_PER_PAGE = 15; // Use 15 per page as requested
  
  // Reset pagination when modal opens or data changes
  useEffect(() => {
    if (isOpen) {
      setCurrentPageNew(1);
      setCurrentPageChanged(1);
    }
  }, [isOpen, validationResult]);
  
  // Process validation data for display (Nutrient specific)
  const processedData = useMemo(() => {
    if (!validationResult?.summary?.nutrientItems?.length) return null;
    
    const items = validationResult.summary.nutrientItems;
    const nutrientSet = new Set();
    const itemsWithNewValues = [];
    const itemsWithChangedValues = [];
    let newValuesCount = 0;
    let changedValuesCount = 0;

    // Separate items and count values
    items.forEach(item => {
      if (!item.nutrients) return; // Skip if no nutrients object
      
      let itemHasNew = false; // Track if the item has at least one new value
      let itemHasChangedOnly = false; // Track if the item only has changed values (no new)
      
      Object.entries(item.nutrients).forEach(([nutrientIdStr, details]) => {
          const nutrientId = parseInt(nutrientIdStr, 10);
          nutrientSet.add(nutrientId); // Add ALL nutrient IDs present to the set for header generation
            
          if (details.isChanged) { // Only count if the nutrient value actually changed
              if (details.existingValue === null || details.existingValue === undefined) {
                  itemHasNew = true;
                  newValuesCount++; // Increment specific counter
              } else {
                  itemHasChangedOnly = true; // Mark that there is at least one change
                  changedValuesCount++; // Increment specific counter
              }
          }
      });
      
      // Assign item to appropriate array based on whether it contains NEW values
      if (itemHasNew) {
        itemsWithNewValues.push(item);
      } else if (itemHasChangedOnly) { // Only add to changed if it ONLY has changes
        itemsWithChangedValues.push(item);
      } 
    });
    
    // Extract nutrient details (name, unit) ONLY for nutrients that appeared in changes
    const nutrientDetailsMap = new Map();
    if (items.length > 0) {
        for (const nutrientId of nutrientSet) {
            const itemWithNutrient = items.find(item => item.nutrients && item.nutrients[nutrientId]);
            if (itemWithNutrient && itemWithNutrient.nutrients[nutrientId]) {
                const nutrientData = itemWithNutrient.nutrients[nutrientId];
                nutrientDetailsMap.set(nutrientId, {
                    name: nutrientData.nutrientName || `Nutrient ${nutrientId}`, // Fallback name
                    unit: nutrientData.unit
                });
            }
        }
    }
    
    // Get sorted list of nutrient IDs
    const sortedNutrientIds = Array.from(nutrientSet).sort((a, b) => a - b); 
    
    return {
      nutrientIds: sortedNutrientIds, 
      nutrientDetails: nutrientDetailsMap, 
      itemsWithNewValues, // Items containing at least one new value
      itemsWithChangedValues, // Items containing only changed values
      summaryCounts: {
          newValues: newValuesCount,
          changedValues: changedValuesCount,
          totalItems: items.length // Total dishes with updates
      }
    };
  }, [validationResult]);
  
  // Calculate paginated data for New Values
  const paginatedNew = useMemo(() => {
    if (!processedData?.itemsWithNewValues) return { items: [], totalPages: 0 };
    const start = (currentPageNew - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const items = processedData.itemsWithNewValues.slice(start, end);
    const totalPages = Math.ceil(processedData.itemsWithNewValues.length / ITEMS_PER_PAGE);
    return { items, totalPages };
  }, [processedData, currentPageNew]);

  // Calculate paginated data for Changed Values
  const paginatedChanged = useMemo(() => {
    if (!processedData?.itemsWithChangedValues) return { items: [], totalPages: 0 };
    const start = (currentPageChanged - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    const items = processedData.itemsWithChangedValues.slice(start, end);
    const totalPages = Math.ceil(processedData.itemsWithChangedValues.length / ITEMS_PER_PAGE);
    return { items, totalPages };
  }, [processedData, currentPageChanged]);
  
  if (!isOpen) return null;
  
  // Handle confirmed import
  const handleImport = () => {
    if (onConfirm && !isImporting && !importResult) {
      onConfirm();
    }
  };
  
  // Handle modal close
  const handleClose = () => {
    if (!isImporting) {
      onClose();
    }
  };
  
  // Determine if there are validation errors
  const hasErrors = validationResult?.summary?.errors && validationResult.summary.errors.length > 0;
  // Determine if the import can proceed (validation ID exists and no errors)
  const canImport = validationResult?.validationId && !hasErrors;

  // Helper to render nutrient cell content
  const renderNutrientCell = (item, nutrientId) => {
      const nutrientDetails = item.nutrients?.[nutrientId]; // Access the details object
      
      // Should not happen if backend sends all nutrient details, but good practice
      if (!nutrientDetails) {
          return <span className="text-gray-400">?</span>; 
      }

      const { value, existingValue, isChanged } = nutrientDetails;
      
      // Determine how to display based on whether it changed and if it has a value
      if (isChanged) {
          const isNew = existingValue === null || existingValue === undefined;
          return (
              <div className={`px-2 py-1 rounded inline-block ${
                  isNew ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800' 
              }`}>
                  <span className="font-semibold">{value}</span>
                  {!isNew && (
                      <span className="text-xs text-gray-500 ml-1 line-through">
                          ({existingValue})
                      </span>
                  )}
              </div>
          );
      } else {
          // Value was not changed by the import file
          // Display the existing value (same as file value if provided and unchanged)
          // Use blue highlighting for consistency
          const displayValue = existingValue ?? <span className="italic text-gray-400">{t('nutrientutils.noValue')}</span>; // Show existing or 'No Value'
          return (
              <span className="bg-blue-50 text-blue-800 px-2 py-1 rounded inline-block">
                  {displayValue}
              </span>
          );
      }
  };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Background overlay */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
      
      {/* Modal content */}
      <div className="flex items-end sm:items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full sm:p-6"> {/* Increased max-width further */}
          {/* Modal header */}
          <div className="sm:flex sm:items-start">
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
              importResult ? 'bg-green-100' : hasErrors ? 'bg-red-100' : 'bg-blue-100'
            }`}>
              {/* Icon based on state */}
              {importResult ? (
                <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
              ) : hasErrors ? (
                 <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              ) : (
                 <svg className="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
              )}
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                {/* Use nutrient-specific translation keys */}
                {importResult ? t('nutrientutils.importResults') : validationResult ? t('nutrientutils.validationModalTitle') : modalTitle || 'Validation Summary'} 
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {importResult 
                    ? t('nutrientutils.importSummary') 
                    : validationResult 
                      ? hasErrors ? (
                        <p className="text-sm font-medium text-red-600">{t('nutrientutils.validationFailedSummary')}</p>
                      ) : (
                        <p className="text-sm font-medium text-green-600">{t('nutrientutils.validationSuccessSummary')}</p>
                      )
                    : t('nutrientutils.validationSuccessSummary')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Modal body */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            {importResult ? (
              // Display Import results
              <div className="space-y-3">
                 <div className={`border rounded-md p-3 ${importResult.success ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                   <div className="flex">
                     <div className="flex-shrink-0">
                       {importResult.success ? (
                         <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                       ) : (
                         <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                       )}
                     </div>
                     <div className="ml-3">
                       <h3 className={`text-sm font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                         {importResult.success ? successMessage : t('common.error')}
                       </h3>
                       {importResult.message && (
                         <p className={`mt-1 text-sm ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>{importResult.message}</p>
                       )}
                     </div>
                   </div>
                 </div>
                 {importResult.stats && (
                     <div className="bg-gray-50 rounded-md p-4">
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                            {/* TODO: Add relevant stats here based on what importNutrientData returns */}
                        </dl>
                     </div>
                 )}
              </div>
            ) : (
              // Display Validation results
              <div className="space-y-3">
                {validationResult?.summary && !hasErrors && processedData && (
                  <div className="bg-gray-50 rounded-md p-4">
                    {/* Updated Summary Section */}
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-4">
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                           {t('nutrientutils.fileName')}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 truncate">
                          {validationResult.summary.fileName || 'N/A'}
                        </dd>
                      </div>
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                           {t('nutrientutils.dishesWithUpdates', { count: processedData.summaryCounts.totalItems })}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                           {processedData.summaryCounts.totalItems}
                        </dd>
                      </div>
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                           {t('nutrientutils.newValuesCount', { count: processedData.summaryCounts.newValues })}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                           {processedData.summaryCounts.newValues}
                        </dd>
                      </div>
                       <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">
                           {t('nutrientutils.changedValuesCount', { count: processedData.summaryCounts.changedValues })}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                           {processedData.summaryCounts.changedValues}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
                
                {/* Display Validation errors/warnings if any */}
                {hasErrors && (
                  <div className="bg-red-50 border border-red-100 rounded-md p-3 max-h-60 overflow-y-auto">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          {t('nutrientutils.errorsFound', { count: validationResult.summary.errors.length })}
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <ul className="list-disc pl-5 space-y-1">
                            {validationResult.summary.errors.map((error, index) => (
                              <li key={index}>
                                {error.row && error.row !== 'multiple' 
                                  ? `${t('nutrientutils.rowError', { row: error.row })} - ` 
                                  : ''}
                                {error.column ? `(${error.column}) ` : ''} 
                                {error.error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                 {/* Section for NEW values */}
                {paginatedNew?.items?.length > 0 && !hasErrors && processedData && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {t('nutrientutils.newValuesHeading', { count: processedData.itemsWithNewValues.length })}
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                      <div className="overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                          <div className="max-h-96 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                   <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-20">
                                     {t('nutrientutils.dishIdHeader', { defaultValue: 'Dish ID' })}
                                   </th>
                                   <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[80px] bg-gray-50 z-20"> {/* Adjust left offset */}
                                      {t('excelHeaders.referenceName')}
                                   </th>
                                   {/* Nutrient Headers */}
                                   {processedData.nutrientIds.map(nutrientId => {
                                     const details = processedData.nutrientDetails.get(nutrientId);
                                     const headerText = details?.unit ? `${details.name} (${details.unit})` : details?.name || `Nutrient ${nutrientId}`;
                                     return (
                                         <th key={nutrientId} scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                         {headerText}
                                         </th>
                                     );
                                   })}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedNew.items.map(item => (
                                  <tr key={`new-${item.id}`}>
                                     <td className="px-3 py-2 text-xs text-gray-700 font-medium whitespace-nowrap sticky left-0 bg-white z-10">
                                        {item.id}
                                     </td>
                                     <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap sticky left-[80px] bg-white z-10"> {/* Adjust left offset */}
                                        {item.referenceName}
                                     </td>
                                     {/* Nutrient Values */}
                                     {processedData.nutrientIds.map(nutrientId => (
                                         <td key={`new-${item.id}-${nutrientId}`} className="px-3 py-2 text-xs break-words text-center">
                                             {renderNutrientCell(item, nutrientId)}
                                         </td>
                                     ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    {paginatedNew.totalPages > 1 && (
                      <PaginationControls 
                        currentPage={currentPageNew}
                        totalPages={paginatedNew.totalPages}
                        onPageChange={setCurrentPageNew}
                        totalItems={processedData.itemsWithNewValues.length}
                      />
                    )}
                  </div>
                )}

                {/* Section for CHANGED values */}
                {paginatedChanged?.items?.length > 0 && !hasErrors && processedData && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                       {t('nutrientutils.changedValuesHeading', { count: processedData.itemsWithChangedValues.length })}
                    </h4>
                    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                      <div className="overflow-x-auto">
                        <div className="inline-block min-w-full align-middle">
                          <div className="max-h-96 overflow-y-auto">
                             <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                   <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-20">
                                     {t('nutrientutils.dishIdHeader', { defaultValue: 'Dish ID' })}
                                   </th>
                                   <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[80px] bg-gray-50 z-20"> {/* Adjust left offset */}
                                      {t('excelHeaders.referenceName')}
                                   </th>
                                   {/* Nutrient Headers */}
                                   {processedData.nutrientIds.map(nutrientId => {
                                     const details = processedData.nutrientDetails.get(nutrientId);
                                     const headerText = details?.unit ? `${details.name} (${details.unit})` : details?.name || `Nutrient ${nutrientId}`;
                                     return (
                                         <th key={nutrientId} scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                         {headerText}
                                         </th>
                                     );
                                   })}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedChanged.items.map(item => (
                                  <tr key={`changed-${item.id}`}>
                                     <td className="px-3 py-2 text-xs text-gray-700 font-medium whitespace-nowrap sticky left-0 bg-white z-10">
                                        {item.id}
                                     </td>
                                     <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap sticky left-[80px] bg-white z-10"> {/* Adjust left offset */}
                                        {item.referenceName}
                                     </td>
                                     {/* Nutrient Values */}
                                     {processedData.nutrientIds.map(nutrientId => (
                                         <td key={`changed-${item.id}-${nutrientId}`} className="px-3 py-2 text-xs break-words text-center">
                                             {renderNutrientCell(item, nutrientId)}
                                         </td>
                                     ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                     {paginatedChanged.totalPages > 1 && (
                      <PaginationControls 
                        currentPage={currentPageChanged}
                        totalPages={paginatedChanged.totalPages}
                        onPageChange={setCurrentPageChanged}
                        totalItems={processedData.itemsWithChangedValues.length}
                      />
                    )}
                  </div>
                )}
                
                {/* Display Validation success message */}
                {canImport && !hasErrors && (
                  <div className="bg-green-50 border border-green-100 rounded-md p-3 mt-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">
                          {t('nutrientutils.fileValid')}
                        </h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p>
                            {t('nutrientutils.readyToImport')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Display Importing indicator */}
            {isImporting && (
              <div className="flex justify-center items-center my-4">
                <svg className="animate-spin h-5 w-5 text-blue-500 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('common.importing')}</span>
              </div>
            )}
          </div>
          
          {/* Modal footer */}
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            {/* Show Confirm button only if import is possible and not already done */}
            {canImport && !importResult && (
              <button
                type="button"
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${
                  isImporting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleImport}
                disabled={isImporting}
              >
                {confirmButtonText}
              </button>
            )}
            
            {/* Close/Cancel button */}
            <button
              type="button"
              className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm ${
                isImporting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={handleClose}
              disabled={isImporting}
            >
              {importResult ? t('common.close') : t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Pagination Controls Component (Keep as is, adjust ITEMS_PER_PAGE if needed)
const PaginationControls = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  const { t } = useTranslation();
  const ITEMS_PER_PAGE = 15; // Ensure this matches the modal's setting

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(startItem + ITEMS_PER_PAGE - 1, totalItems);

  return (
    <nav
      className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-2"
      aria-label="Pagination"
    >
      <div className="hidden sm:block">
        <p className="text-sm text-gray-700">
          {t('pagination.showing')} <span className="font-medium">{startItem}</span> {t('pagination.to')} <span className="font-medium">{endItem}</span> {t('pagination.of')} <span className="font-medium">{totalItems}</span> {t('pagination.results')}
        </p>
      </div>
      <div className="flex flex-1 justify-between sm:justify-end">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('pagination.previous')}
        </button>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('pagination.next')}
        </button>
      </div>
    </nav>
  );
};

export default NutrientValidationSummaryModal; 